import { create } from 'zustand';
import {
  getFacultyLabInternals,
  getFacultyLabInternalDetails,
  triggerFacultyLabEvaluation,
  getFacultyStudentEvaluation,
  triggerManualEvaluation,
  getFacultyAllowedQuestions,
  setFacultyAllowedQuestions,
  deleteFacultyLabInternal,
  getStudentLabInternals,
  getStudentLabQuestions,
  lockStudentLabQuestions,
  getStudentLabInternalDashboard,
  submitStudentTest,
} from '../services/labInternals.api';

const useLabInternalStore = create((set, get) => ({
  // ── Faculty state ──
  facultyLabInternals: [],
  facultyLoading: false,
  facultyError: null,

  facultyLabDetails: {}, // map labId -> details object
  facultyDetailsLoading: false,
  facultyDetailsError: null,

  facultyAllowedQuestions: {}, // map labId -> array
  facultyQuestionsLoading: false,
  facultyQuestionsError: null,

  // ── Student state ──
  studentLabInternals: [],
  studentLoading: false,
  studentError: null,

  studentLabDashboard: {}, // map labId -> dashboard
  studentDashboardLoading: false,
  studentDashboardError: null,

  studentLabQuestions: {}, // map labId -> array
  studentQuestionsLoading: false,
  studentQuestionsError: null,

  // ── Faculty actions ──
  async fetchFacultyLabInternals() {
    set({ facultyLoading: true, facultyError: null });
    try {
      const labs = await getFacultyLabInternals();
      const list = Array.isArray(labs) ? labs : [];
      set({ facultyLabInternals: list, facultyLoading: false });
      return list;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to load lab internals';
      set({ facultyError: message, facultyLoading: false });
      throw err;
    }
  },

  async fetchFacultyLabDetails(labId) {
    set({ facultyDetailsLoading: true, facultyDetailsError: null });
    try {
      const details = await getFacultyLabInternalDetails(labId);
      set((state) => ({
        facultyLabDetails: { ...state.facultyLabDetails, [labId]: details },
        facultyDetailsLoading: false,
      }));
      return details;
    } catch (err) {
      set({ facultyDetailsError: err, facultyDetailsLoading: false });
      throw err;
    }
  },

  async fetchFacultyAllowedQuestions(labId) {
    set({ facultyQuestionsLoading: true, facultyQuestionsError: null });
    try {
      const questions = await getFacultyAllowedQuestions(labId);
      set((state) => ({
        facultyAllowedQuestions: { ...state.facultyAllowedQuestions, [labId]: questions?.data || questions },
        facultyQuestionsLoading: false,
      }));
      return questions;
    } catch (err) {
      set({ facultyQuestionsError: err, facultyQuestionsLoading: false });
      throw err;
    }
  },

  async setFacultyAllowedQuestions(labId, questions) {
    await setFacultyAllowedQuestions(labId, questions);
    // Refresh allowed questions after setting
    await get().fetchFacultyAllowedQuestions(labId);
  },

  async triggerFacultyLabEvaluation(labId) {
    return await triggerFacultyLabEvaluation(labId);
  },

  async deleteFacultyLabInternal(labId) {
    await deleteFacultyLabInternal(labId);
    await get().fetchFacultyLabInternals();
  },

  async fetchFacultyStudentEvaluation(labId, studentLabId) {
    return await getFacultyStudentEvaluation(labId, studentLabId);
  },

  async triggerManualEvaluation(studentLabIds) {
    return await triggerManualEvaluation(studentLabIds);
  },

  // ── Student actions ──
  async fetchStudentLabInternals() {
    set({ studentLoading: true, studentError: null });
    try {
      const labs = await getStudentLabInternals();
      set({ studentLabInternals: labs, studentLoading: false });
      return labs;
    } catch (err) {
      set({ studentError: err, studentLoading: false });
      throw err;
    }
  },

  async fetchStudentLabDashboard(labId) {
    set({ studentDashboardLoading: true, studentDashboardError: null });
    try {
      const dashboard = await getStudentLabInternalDashboard(labId);
      set((state) => ({
        studentLabDashboard: { ...state.studentLabDashboard, [labId]: dashboard },
        studentDashboardLoading: false,
      }));
      return dashboard;
    } catch (err) {
      set({ studentDashboardError: err, studentDashboardLoading: false });
      throw err;
    }
  },

  async fetchStudentLabQuestions(labId) {
    set({ studentQuestionsLoading: true, studentQuestionsError: null });
    try {
      const questions = await getStudentLabQuestions(labId);
      set((state) => ({
        studentLabQuestions: { ...state.studentLabQuestions, [labId]: questions },
        studentQuestionsLoading: false,
      }));
      return questions;
    } catch (err) {
      set({ studentQuestionsError: err, studentQuestionsLoading: false });
      throw err;
    }
  },

  async lockStudentLabQuestions(labId, payload) {
    const response = await lockStudentLabQuestions(labId, payload);
    await get().fetchStudentLabDashboard(response?.data?.labId);
    await get().fetchStudentLabQuestions(labId);
    return response;
  },

  async submitStudentTest(labId, payload) {
    return await submitStudentTest(labId, payload);
  },
}));

export default useLabInternalStore;
